export const SHOP_SECTION_QUERY_PARAM = "section";
export const SHOP_ITEM_QUERY_PARAM = "itemId";

export type ShopSection = "gift-cards" | "packages";

export const SHOP_SECTIONS = {
  giftCards: "gift-cards",
  packages: "packages",
} as const satisfies Record<string, ShopSection>;

function getShopQuery(
  section?: ShopSection | null,
  itemId?: string | null,
): string {
  const searchParams = new URLSearchParams();

  if (section) searchParams.set(SHOP_SECTION_QUERY_PARAM, section);
  if (itemId?.trim()) searchParams.set(SHOP_ITEM_QUERY_PARAM, itemId.trim());

  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function getShopPath(
  tenant: string,
  section?: ShopSection | null,
  itemId?: string | null,
  embed = false,
): string {
  return `/${tenant}/${embed ? "embed/" : ""}shop${getShopQuery(section, itemId)}`;
}

export function getShopUrl(
  tenantBaseUrl: string,
  section?: ShopSection | null,
  itemId?: string | null,
): string {
  return `${tenantBaseUrl}/shop${getShopQuery(section, itemId)}`;
}

export function parseShopSection(value: string | null): ShopSection | null {
  if (value === SHOP_SECTIONS.giftCards || value === SHOP_SECTIONS.packages) {
    return value;
  }

  return null;
}

export function parseShopItemId(value: string | null): string | null {
  const itemId = value?.trim();
  return itemId || null;
}

export function resolveShopLinkTargets(
  sectionValue: string | null,
  itemIdValue: string | null,
): { initialSection: ShopSection | null; featuredItemId: string | null } {
  const featuredItemId = parseShopItemId(itemIdValue);

  return {
    featuredItemId,
    initialSection: parseShopSection(sectionValue),
  };
}

export const SHOP_CATEGORY_QUERY_PARAM = "category";
export const SHOP_SORT_QUERY_PARAM = "sort";
export const SHOP_CHECKOUT_QUERY_PARAM = "checkout";

/** Browse state carried between the shop grid and a product page. */
export interface ShopBrowseState {
  category?: string | null;
  sort?: string | null;
}

function browseQuery(
  state: ShopBrowseState,
  extra?: Record<string, string>,
): string {
  const searchParams = new URLSearchParams();
  if (state.category) {
    searchParams.set(SHOP_CATEGORY_QUERY_PARAM, state.category);
  }
  if (state.sort) searchParams.set(SHOP_SORT_QUERY_PARAM, state.sort);
  for (const [key, value] of Object.entries(extra ?? {})) {
    searchParams.set(key, value);
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function getShopBrowsePath(
  tenant: string,
  state: ShopBrowseState = {},
  options: { embed?: boolean; checkout?: boolean } = {},
): string {
  return `/${tenant}/${options.embed ? "embed/" : ""}shop${browseQuery(
    state,
    options.checkout ? { [SHOP_CHECKOUT_QUERY_PARAM]: "1" } : undefined,
  )}`;
}

export function getShopItemPagePath(
  tenant: string,
  itemId: string,
  state: ShopBrowseState = {},
  embed = false,
): string {
  return `/${tenant}/${embed ? "embed/" : ""}shop/item/${encodeURIComponent(
    itemId,
  )}${browseQuery(state)}`;
}

export function isEmbeddedShopPath(pathname: string | null | undefined) {
  return Boolean(pathname?.includes("/embed/shop"));
}
